import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { toast, ToastContainer, Bounce } from 'react-toastify';
import curbsideAPIService from '../services/curbsideAPIService';

 export function handleAccountSetupEffect(rawToken: string | null, action : string | null, {
    activateUser = () => {},
    setCanSetPassword = (_: boolean) => {},
    setLoading = (_: boolean) => {},
    setActiveToken = (_: any) => {},
    setIsExpired = (_: boolean) => {}
}) {
    if (!rawToken) {
        setIsExpired(true);
        setLoading(false);
        return;
    }
    if (action === '1_activate') {
        activateUser();
    }
    else if (action === 'set-new-password') {
        setCanSetPassword(true);
        setLoading(false);
        setActiveToken(rawToken);
    }
    else {
        setIsExpired(true);
        setLoading(false);
    }
}




export function validatePassword(password: string): string | null {
    if (!password) return 'Password is required';

    const rules = [
        {
            test: (pw: string) => pw.length >= 12,
            message: 'Password must be at least 12 characters long',
        },
        {
            test: (pw: string) => /[A-Z]/.test(pw),
            message: 'Include at least one uppercase letter',
        },
        {
            test: (pw: string) => /[a-z]/.test(pw),
            message: 'Include at least one lowercase letter',
        },
        {
            test: (pw: string) => /\d/.test(pw),
            message: 'Include at least one number',
        },
        {
            test: (pw: string) => /[!@#$%^&*(),.?":{}|<>]/.test(pw),
            message: 'Include at least one special character',
        },
    ];

    for (const rule of rules) {
        if (!rule.test(password)) return rule.message;
    }

    return null;
}

export async function activateUserPure({
    token,
    setActiveToken,
    setCanSetPassword,
    setIsExpired,
    setLoading,
    toast,
    curbsideAPIService,
    remoteIP = '',
}: {
    token: string | string[] | null | undefined;
    setActiveToken: (token: any) => void;
    setCanSetPassword: (can: boolean) => void;
    setIsExpired: (expired: boolean) => void;
    setLoading: (loading: boolean) => void;
    toast: typeof import('react-toastify').toast;
    curbsideAPIService: typeof import('../services/curbsideAPIService').default;
    remoteIP?: string;
}) {
    try {
        const response = await curbsideAPIService.post('staff/activateStaff', {
            token,
            remoteIP,
        });

        if (response.status === true && response.data) {
            toast.success('Account activated! Please set your password.');
            setActiveToken(response.data);
            setIsExpired(false);
            setCanSetPassword(true);
        } else {
            toast.error(response.message ?? 'Activation failed.');
            setIsExpired(true);
            setCanSetPassword(false);
        }
    } catch (error) {
        console.error('Activation error:', error);
        toast.error('Activation failed due to an error.');
        setIsExpired(true);
        setCanSetPassword(false);
    }
    setLoading(false);
}
export const clearCookiesAndStorage = () => {
    localStorage.clear();
    sessionStorage.clear();
    document.cookie.split(';').forEach((cookie) => {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.slice(0, eqPos).trim() : cookie.trim();
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
    });
};
export async function handleAccountSetupSubmit({
    e,
    password,
    confirmPassword,
    activeToken,
    setSubmitted,
    navigate,
    toast,
    curbsideAPIService,
    clearCookiesAndStorage,
    setIsExpired,
    setCanSetPassword
}: {
    e: React.FormEvent<HTMLFormElement>;
    password: string;
    confirmPassword: string;
    activeToken: string | null;
    setSubmitted: (submitted: boolean) => void;
    navigate: ReturnType<typeof useNavigate>;
    toast: typeof import('react-toastify').toast;
    curbsideAPIService: typeof import('../services/curbsideAPIService').default;
    clearCookiesAndStorage: () => void;
    setIsExpired: (expired: boolean) => void;
    setCanSetPassword: (expired: boolean) => void;
}) {
    e.preventDefault();

    const passwordError = validatePassword(password);
    if (passwordError) {
        toast.warn(passwordError);
        return;
    }

    if (password !== confirmPassword) {
        toast.warn('Passwords do not match');
        return;
    }

    try {
        const response = await curbsideAPIService.post('staff/setpassword', {
            token: activeToken,
            remoteIP: 'string',
            password,
        });

        if (response.status === true) {
            toast.success('Password set successfully!');
            setSubmitted(true);
            setTimeout(() => {
                clearCookiesAndStorage();
                navigate('/');
            }, 5000);
        } else {
            setIsExpired(true);
            setCanSetPassword(false)
            toast.error(response.message ?? 'Request failed.');
        }
    } catch (error) {
        setIsExpired(true);
        setCanSetPassword(false)
        console.error('Submission error:', error);
        toast.error('Something went wrong.');
    }
}

const useRawToken = () => {
    const location = useLocation();
    const rawQuery = location.search;
    const tokenRegex = /[?&]token=([^&]*)/;
    const actionRegex = /[?&]action=([^&]*)/;
    const tokenMatch = tokenRegex.exec(rawQuery);
    const actionMatch = actionRegex.exec(rawQuery);
    const rawToken = tokenMatch ? tokenMatch[1] : null;
    const action = actionMatch ? actionMatch[1] : null;
    return { rawToken, action };
};



const AccountSetup = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isExpired, setIsExpired] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [canSetPassword, setCanSetPassword] = useState(false);
    const [activeToken, setActiveToken] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);


    const navigate = useNavigate();
    const { rawToken, action } = useRawToken();

    const activateUser = async () => {
        await activateUserPure({
            token: rawToken,
            setActiveToken,
            setCanSetPassword,
            setIsExpired,
            setLoading,
            toast,
            curbsideAPIService,
            remoteIP: '',
        });
    };

    useEffect(() => {
      handleAccountSetupEffect(rawToken, action, {
        activateUser: () => { void activateUser(); },
        setCanSetPassword,
        setLoading,
        setActiveToken,
        setIsExpired
        });
    }, [rawToken, action]);



    const renderLoader = () => (
        <div className="flex items-center justify-center w-full h-screen bg-gray-100">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
        </div>
    );

    if (loading) return renderLoader();
    return (
        <div className="bg-gray-50 flex items-center justify-center min-h-screen">
            {isExpired && (
                <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md text-center">
                    <h1 className="text-3xl font-bold mb-4 text-red-600">Link Expired</h1>
                    <p className="text-lg text-gray-700">
                        This link is no longer valid. Please contact support or request a new one.
                    </p>
                </div>
            )}
            {(!submitted && canSetPassword) && (
                <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md text-center">
                    <h1 className="text-3xl font-extrabold text-gray-900 mb-4">Set Your New Password</h1>
                    <p className="text-base text-gray-600 mb-3">
                        {action === '1_activate'
                            ? 'Your account has been activated. Please create a secure password to complete your setup.'
                            : 'Please create a new password to reset your account.'}
                    </p>

                    <form
                        onSubmit={(e) => {
                            handleAccountSetupSubmit({
                                e,
                                password,
                                confirmPassword,
                                activeToken,
                                setSubmitted,
                                navigate,
                                toast,
                                curbsideAPIService,
                                clearCookiesAndStorage,
                                setIsExpired,
                                setCanSetPassword
                            });
                        }}
                        className="space-y-6 mt-6"
                    >
                        <div className="relative">
                            <label htmlFor="password" className="block text-left text-sm font-medium text-gray-700 mb-2">
                                New Password
                            </label>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter new password"
                                className="w-full pr-10 px-4 py-3 border rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((show) => !show)}
                                className="absolute bottom-[13px] right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                            </button>
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-left text-sm font-medium text-gray-700 mb-2">
                                Confirm Password
                            </label>
                            <input
                                type="password"
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                                className="w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>

                        <button
                            type="submit"
                            className="w-full px-4 py-3 bg-indigo-600 text-white rounded-md font-semibold hover:bg-indigo-700 transition duration-300"
                        >
                            Set Password
                        </button>
                    </form>
                </div>
            )}
            {submitted && !isExpired && (
                <div className="bg-white w-full max-w-md mx-auto p-6 sm:p-8 rounded-2xl shadow-xl text-center">
                    <div className="flex justify-center mb-6 text-green-500">
                        <i className="fas fa-check-circle text-6xl sm:text-7xl"></i>
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-3">Password Saved</h1>
                    <p className="text-sm sm:text-base text-gray-600">
                        Your password has been successfully updated. You’ll be redirected to the login page shortly.
                    </p>
                </div>
            )}
            <ToastContainer position="top-center" autoClose={5000} hideProgressBar transition={Bounce} />
        </div>
    );
};

export default AccountSetup;